import { useCallback, useEffect, useRef, useState } from "react";
import { createCar, updateCar } from "../api/cars";
import { createAccessory, updateAccessory } from "../api/accessories";
import { createSpare, updateSpare } from "../api/spares";

const MAX_IMAGES = 15;

const VEHICLE_DEFAULTS = {
  make: "",
  model: "",
  year: "",
  body_type: "",
  condition: "",
  location: "",
  engine: "",
  transmission: "",
  fuel_type: "",
  fuel_consumption: "",
  drive_train: "",
  color: "",
  mileage: "",
  mileage_unit: "km",
  doors: "",
  seats: "",
  price: "",
  currency: "",
  badge: "",
  description: "",
  features: "",
  inspectionReport: null,
};

export const EMPTY_INSPECTION_REPORT = {
  reportDate: "",
  validUntil: "",
  plateNumber: "",
  vinNumber: "",
  makeModelYear: "",
  engineCc: "",
  powerFeatures: { windows: "", mirrors: "", lighting: "", dashboard: "", airConditioning: "" },
  additionalFeatures: "",
  infotainmentFeatures: "",
  interiorCondition: "",
  exteriorCondition: "",
  accidentHistory: "",
  wheelWear: "",
  engineCondition: "",
  coolingSystem: "",
  electricalSystem: "",
  engineSystem: "",
  startingSystem: "",
  chargingSystem: "",
  transmissionSystem: "",
  brakeSystem: "",
  steeringSystem: "",
  fuelSystem: "",
  disclaimer: "The Vehicle Inspection Report is provided solely as a general assessment of the vehicle's condition at the time it is received from the registered owner. It is not a warranty or guarantee of the vehicle's condition. Buyers should independently inspect and verify the vehicle before purchase.",
};

const ACCESSORY_DEFAULTS = {
  category: "",
  brand: "",
  name: "",
  sku: "",
  price: "",
  currency: "",
  stock: "",
  badge: "",
  compatibility: "",
  description: "",
};

const SPARE_DEFAULTS = {
  category: "",
  name: "",
  sku: "",
  price: "",
  currency: "",
  quantity: "",
  supplier: "",
  manufacturer: "",
  badge: "",
  description: "",
};

const HIRE_DEFAULTS = {
  make: "",
  model: "",
  year: "",
  location: "",
  transmission: "",
  fuel_type: "",
  fuel_consumption: "",
  seats: "",
  driver_option: "",
  price: "",
  currency: "KES",
  min_hire_days: "",
  mileage_limit: "",
  deposit: "",
  availability: "available",
  badge: "",
  description: "",
  features: "",
};

const DEFAULTS_BY_TYPE = {
  vehicle: VEHICLE_DEFAULTS,
  accessory: ACCESSORY_DEFAULTS,
  spare: SPARE_DEFAULTS,
  hire: HIRE_DEFAULTS,
};

const REQUIRED_FIELDS_BY_TYPE = {
  vehicle: ["make", "model", "year", "condition", "location", "price"],
  accessory: ["category", "brand", "name", "price", "stock"],
  spare: ["category", "name", "price", "quantity"],
  hire: ["make", "model", "year", "location", "driver_option", "price"],
};

const FIELD_LABELS = {
  make: "Make",
  model: "Model",
  year: "Year",
  condition: "Condition",
  location: "Location",
  price: "Price",
  category: "Category",
  brand: "Brand",
  name: "Product name",
  stock: "Stock quantity",
  quantity: "Quantity",
  driver_option: "Driver option",
};

const FIELD_VALIDATORS = {
  year: (value) => {
    if (!value) return null;
    const year = Number(value);
    const maxYear = new Date().getFullYear() + 1;
    if (Number.isNaN(year) || year < 1980 || year > maxYear) {
      return `Year must be between 1980 and ${maxYear}`;
    }
    return null;
  },
  price: (value) => (value && Number(value) < 0 ? "Price cannot be negative" : null),
  stock: (value) => (value && Number(value) < 0 ? "Stock cannot be negative" : null),
  quantity: (value) => (value && Number(value) < 0 ? "Quantity cannot be negative" : null),
};

const NUMERIC_FIELDS = ["year", "mileage", "doors", "seats", "price", "stock", "quantity", "min_hire_days", "mileage_limit", "deposit", "fuel_consumption"];
const LIST_FIELDS = ["features", "compatibility"];

const SUBMIT_HANDLERS = {
  vehicle: createCar,
  accessory: createAccessory,
  spare: createSpare,
  hire: createCar,
};

const UPDATE_HANDLERS = {
  vehicle: updateCar,
  accessory: updateAccessory,
  spare: updateSpare,
  hire: updateCar,
};

function listingToForm(listingType, listing) {
  if (listingType === "vehicle" || listingType === "hire") {
    return {
      ...DEFAULTS_BY_TYPE[listingType],
      make: listing.make || "",
      model: listing.model || "",
      year: listing.year ?? "",
      location: listing.location || "",
      transmission: listing.transmission || "",
      fuel_type: listing.fuelType || "",
      fuel_consumption: listing.fuelConsumption ?? "",
      seats: listing.seats ?? "",
      price: listing.price ?? "",
      currency: listing.currency || "KES",
      condition: listing.condition || "",
      body_type: listing.bodyType || "",
      mileage: listing.mileage ?? "",
      driver_option: listing.driverOption || "",
      min_hire_days: listing.minHireDays ?? "",
      mileage_limit: listing.mileageLimit ?? "",
      deposit: listing.deposit ?? "",
      availability: listing.availability || "available",
      badge: listing.badge || "",
      description: listing.description || "",
      features: Array.isArray(listing.features) ? listing.features.join(", ") : "",
      inspectionReport: listing.inspectionReport ? { ...EMPTY_INSPECTION_REPORT, ...listing.inspectionReport, powerFeatures: { ...EMPTY_INSPECTION_REPORT.powerFeatures, ...(listing.inspectionReport.powerFeatures || {}) } } : null,
    };
  }

  if (listingType === "accessory") {
    return {
      ...ACCESSORY_DEFAULTS,
      category: listing.category || "",
      name: listing.title || "",
      price: listing.price ?? "",
      currency: listing.currency || "KES",
      compatibility: Array.isArray(listing.features) ? listing.features.join(", ") : "",
      description: listing.description || "",
    };
  }

  return {
    ...SPARE_DEFAULTS,
    category: listing.category || "",
    name: listing.name || "",
    sku: listing.sku || "",
    price: listing.price ?? "",
    currency: listing.currency || "KES",
    quantity: listing.quantity ?? "",
    supplier: listing.supplier || "",
    manufacturer: listing.manufacturer || "",
    description: listing.description || "",
  };
}

function validateForm(listingType, form) {
  const errors = {};

  REQUIRED_FIELDS_BY_TYPE[listingType].forEach((field) => {
    if (!String(form[field] ?? "").trim()) {
      errors[field] = `${FIELD_LABELS[field] || field} is required`;
    }
  });

  Object.entries(FIELD_VALIDATORS).forEach(([field, validate]) => {
    if (errors[field]) return;
    const message = validate(form[field]);
    if (message) errors[field] = message;
  });

  return errors;
}

function toNumberOrUndefined(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
}

function toListOrUndefined(value) {
  if (!value) return undefined;
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function buildPayload(listingType, form, images) {
  let payload = {};

  if (listingType === "vehicle") {
    payload = {
      make: String(form.make ?? '').trim() || undefined,
      model: String(form.model ?? '').trim() || undefined,
      year: toNumberOrUndefined(form.year),
      price: toNumberOrUndefined(form.price),
      currency: String(form.currency ?? 'KES').trim() || 'KES',
      mileage: toNumberOrUndefined(form.mileage),
      condition: String(form.condition ?? '').trim() || undefined,
      transmission: String(form.transmission ?? '').trim() || undefined,
      fuelType: String(form.fuel_type ?? '').trim() || undefined,
      fuelConsumption: toNumberOrUndefined(form.fuel_consumption),
      bodyType: String(form.body_type ?? '').trim() || undefined,
      location: String(form.location ?? '').trim() || undefined,
      description: String(form.description ?? '').trim() || undefined,
      features: toListOrUndefined(form.features) || [],
      tags: toListOrUndefined(form.tags) || [],
      images: images.filter(Boolean),
      inspectionReport: form.inspectionReport || null,
    };
  } else if (listingType === "accessory") {
    payload = {
      title: String(form.name ?? '').trim() || undefined,
      category: String(form.category ?? '').trim() || undefined,
      price: toNumberOrUndefined(form.price),
      currency: String(form.currency ?? 'KES').trim() || 'KES',
      description: String(form.description ?? '').trim() || undefined,
      features: toListOrUndefined(form.compatibility) || [],
      tags: toListOrUndefined(form.tags) || [],
      images: images.filter(Boolean),
    };
  } else if (listingType === "spare") {
    payload = {
      name: String(form.name ?? '').trim() || undefined,
      category: String(form.category ?? '').trim() || undefined,
      sku: String(form.sku ?? '').trim() || undefined,
      price: toNumberOrUndefined(form.price),
      currency: String(form.currency ?? 'KES').trim() || 'KES',
      quantity: toNumberOrUndefined(form.quantity),
      supplier: String(form.supplier ?? '').trim() || undefined,
      manufacturer: String(form.manufacturer ?? '').trim() || undefined,
      description: String(form.description ?? '').trim() || undefined,
      tags: toListOrUndefined(form.tags) || [],
      images: images.filter(Boolean),
    };
  } else if (listingType === "hire") {
    payload = {
      type: "hire",
      make: String(form.make ?? "").trim() || undefined,
      model: String(form.model ?? "").trim() || undefined,
      year: toNumberOrUndefined(form.year),
      price: toNumberOrUndefined(form.price),
      currency: String(form.currency ?? "KES").trim() || "KES",
      location: String(form.location ?? "").trim() || undefined,
      transmission: String(form.transmission ?? "").trim() || undefined,
      fuelType: String(form.fuel_type ?? "").trim() || undefined,
      fuelConsumption: toNumberOrUndefined(form.fuel_consumption),
      seats: toNumberOrUndefined(form.seats),
      driver_option: String(form.driver_option ?? "").trim() || undefined,
      min_hire_days: toNumberOrUndefined(form.min_hire_days),
      mileage_limit: toNumberOrUndefined(form.mileage_limit),
      deposit: toNumberOrUndefined(form.deposit),
      availability: String(form.availability ?? "available").trim() || "available",
      badge: String(form.badge ?? "").trim() || undefined,
      description: String(form.description ?? "").trim() || undefined,
      features: toListOrUndefined(form.features) || [],
      tags: [],
      images: images.filter(Boolean),
    };
  }

  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (value === undefined || value === '') {
      delete payload[key];
    }
  });

  return payload;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function useAddListing({ onSubmit } = {}) {
  const [listingType, setListingType] = useState("vehicle");
  const [form, setForm] = useState(VEHICLE_DEFAULTS);
  const [errors, setErrors] = useState({});
  const [images, setImages] = useState([]); // [{ file, previewUrl }]
  const [accessorySpecs, setAccessorySpecs] = useState([{ key: "", value: "" }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  const switchType = useCallback((type) => {
    imagesRef.current.forEach((img) => {
      if (img.file) URL.revokeObjectURL(img.previewUrl);
    });
    setListingType(type);
    setForm(DEFAULTS_BY_TYPE[type]);
    setEditingId(null);
    setImages([]);
    setErrors({});
    setSubmitSuccess(false);
  }, []);

  const startEditing = useCallback((type, listing) => {
    imagesRef.current.forEach((img) => {
      if (img.file) URL.revokeObjectURL(img.previewUrl);
    });
    setListingType(type);
    setEditingId(listing.id);
    setForm(listingToForm(type, listing));
    setImages((listing.images || []).map((previewUrl) => ({ file: null, previewUrl })));
    setAccessorySpecs([{ key: "", value: "" }]);
    setErrors({});
    setSubmitSuccess(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key] && !prev._submit) return prev;
      const next = { ...prev };
      delete next[key];
      delete next._submit;
      return next;
    });
  }, []);

  const handleImages = useCallback((fileList) => {
  const picked = Array.from(fileList || []);   // copy first: FileList is live
  const room = Math.max(0, MAX_IMAGES - imagesRef.current.length);
  const incoming = picked.slice(0, room).map((file) => ({
    file,
    previewUrl: URL.createObjectURL(file),
  }));

  if (!incoming.length) return;

  imagesRef.current = [...imagesRef.current, ...incoming]; // keeps the room check right on quick repeat adds
  setImages((prev) => [...prev, ...incoming]);             // pure merge, safe if React runs it twice
}, []);

  const removeImage = useCallback((index) => {
    setImages((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addSpecRow = useCallback(() => {
    setAccessorySpecs((prev) => [...prev, { key: "", value: "" }]);
  }, []);

  const removeSpecRow = useCallback((index) => {
    setAccessorySpecs((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }, []);

  const updateSpecRow = useCallback((index, field, value) => {
    setAccessorySpecs((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }, []);

  const resetForm = useCallback(() => {
    imagesRef.current.forEach((img) => {
      if (img.file) URL.revokeObjectURL(img.previewUrl);
    });
    setImages([]);
    setForm(DEFAULTS_BY_TYPE[listingType]);
    setAccessorySpecs([{ key: "", value: "" }]);
    setEditingId(null);
    setErrors({});
    setIsSubmitting(false);
    setSubmitSuccess(false);
  }, [listingType]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const validationErrors = validateForm(listingType, form);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({});
      setIsSubmitting(true);

      try {
        const imageDataUrls = await Promise.all(
          images.map((img) => img.file ? fileToDataUrl(img.file) : img.previewUrl)
        );
        const payload = buildPayload(listingType, form, imageDataUrls);
        const savedListing = editingId
          ? await UPDATE_HANDLERS[listingType](editingId, payload)
          : await SUBMIT_HANDLERS[listingType](payload);

        setSubmitSuccess(true);
        if (onSubmit) onSubmit(savedListing);
      } catch (error) {
        setErrors({ _submit: error.message || "Something went wrong. Please try again." });
      } finally {
        setIsSubmitting(false);
      }
    },
    [editingId, listingType, form, images, onSubmit]
  );

  return {
    listingType,
    switchType,
    editingId,
    startEditing,
    form,
    setField,
    errors,
    imagePreviews: images.map((img) => img.previewUrl),
    handleImages,
    removeImage,
    addSpecRow,
    removeSpecRow,
    updateSpecRow,
    accessorySpecs,
    isSubmitting,
    submitSuccess,
    handleSubmit,
    resetForm,
  };
}